<script>
export default {
  title: 'Project 2: Python-Powered Unit Converter',
  starterId: 'proj2.zip'
}
</script>

<template>
  <section>
    <Readings :readings="[
      {
        title: '<cite>Think Python (2nd Edition)</cite> Chapter 10',
        link: 'http://greenteapress.com/thinkpython2/html/thinkpython2011.html'
      },
      {
        title: '<cite>Think Python (2nd Edition)</cite> Chapter 11',
        link: 'http://greenteapress.com/thinkpython2/html/thinkpython2012.html'
      },
      {
        title: '<cite>Think Python (2nd Edition)</cite> Chapter 12',
        link: 'http://greenteapress.com/thinkpython2/html/thinkpython2013.html'
      },
      {
        title: '<cite>The Python Tutorial</cite> 9.2',
        link: 'https://docs.python.org/3/tutorial/classes.html#python-scopes-and-namespaces'
      },
      {
        title: '<cite>Composing Programs</cite> 2.4.2',
        link: 'http://composingprograms.com/pages/24-mutable-data.html#sequence-objects'
      }
    ]"/>

    <article id="background">
      <h2>Historical Background (Optional)</h2>

      <p>
        Let's take a trip back down memory lane. Imagine you're a high-paid engineer for NASA, and the date
        is late September, 1999. The entire agency is all abuzz with the prospect of the
        <a href="https://en.wikipedia.org/wiki/Mars_Climate_Orbiter">Mars Climate Orbiter</a>, launched more
        than nine months ago, reaching a stable orbit around our neighboring red planet and sending back
        important weather data.
      </p>

      <figure>
        <img class="img-responsive center-block" src="./images/unit_converter_mco_mapping.gif" width="200">
        <figcaption>Figure 1. Illustration of the Mars Climate Orbiter</figcaption>
      </figure>

      <p>
        On September 23, 1999, your team loses the signal to this multi-million dollar orbiter. In November,
        after <a href="ftp://ftp.hq.nasa.gov/pub/pao/reports/1999/MCO_report.pdf">a lengthy investigation</a>,
        you <a href="http://www.cnn.com/TECH/space/9909/30/mars.metric.02/index.html">inform the press of the
        reasons for the crash</a>: data in a software file for calculating trajectory forces was in
        <strong>English units</strong> of pound-seconds (lbf-s), but the thruster was <em>expecting</em> the
        data to be in <strong>metric units</strong> of Newton-seconds (N-s).
      </p>

      <p>
        History records the experience as one of the most egregious and
        expensive bugs of all time. Engineers and computer scientists the world
        over vow to be diligent to check, double-check and triple-check the
        <dfn>units</dfn> in their calculations.
      </p>
    </article>

    <article id="starter">
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above. It should contains two file
        called <code>converter.py</code> and <code>autograder.py</code>.
      </p>
    </article>

    <article id="task">
      <h2>Your Task</h2>

      <p>For this project, you'll write a Python program to do <dfn>unit
        conversion</dfn>&mdash;provide the translation of a quantity specified by a
        certain amount in one <em>source</em> unit and a <em>destination</em> unit.
        That is, your program should be able to answer the common question (and
        many more like it): <em>How many meters in 10 miles?</em> (answer: 10 miles
        = 16,093.44 m)</p>

      <p>To make the input easy, you'll specify your queries in a simplified
        format:</p>

      <pre v-highlightjs><code class="plain"><em>AMOUNT</em> <em>SOURCE_UNIT</em> in <em>DESTINATION_UNIT</em></code></pre>

      <p>...which will make the understanding, or <em>parsing</em> of the input
        much easier. As an example, this is the way you would specify your earlier
        request:</p>

      <pre v-highlightjs><code class="plain">10 mi in m</code></pre>

      <p>to which your program should respond:</p>

      <pre v-highlightjs><code class="plain">10 mi = 16093.440000 m</code></pre>

    </article>

    <article id="requirements">
      <h2>Requirements</h2>

      <p>Your text interface doesn't have to be fancy, but it does need to have
        a few key features:</p>

      <ul>
        <li>
          <p><b>Welcome text for your program</b>, listing your name, the program's use, and its input format</p>
        </li>
        <li>
          <p><b>A list of all the categories and units within that category</b> you support so the user knows what they can do</p>
        </li>
        <li>
          <p><b>A repeating loop</b> that asks for conversions over an over in the format we described above.</p>
        </li>
        <li>
          <p>If the input is valid, output the result in the format of X UNIT_1 = Y UNIT_2.
            X should exactly match the user input and Y should keep <b>6 decimal places</b>.
          </p>
        </li>
        <li>
          <p>
            If the user input is not exactly right, a <em>friendly, helpful</em> error message should be
            printed along with the prompt again. The message <b>should start with "Error: "</b>, followed by
            any message you like.
          </p>
        </li>
        <li>
          <p><b>The program should not crash</b> under any user input (even those illegal ones). The loop should run
            indefinitely and the only way to quit your program should be entering <code>q</code> as the user input.</p>
        </li>
      </ul>

      <p>
        For example, here is our interface. Note that your program doesn't have to look exactly like ours,
        it only needs to adhere to the checklist above (and the final checklist below)
      </p>

      <p>If you run the following command:</p>

      <pre v-highlightjs><code class="bash">$ python3 converter.py</code></pre>

      <p>You should get (text in bold are user inputs):</p>

      <pre v-highlightjs><code class="plain">Welcome to our Python-powered Unit Converter v1.0 by Dan Garcia!
You can convert Distances, Weights, Volumes to one another, but only
within units of the same category, which are shown below. E.g.: 1 mi in ft

 Distances: ft cm mm mi m yd km in
   Weights: lb mg kg oz g
   Volumes: floz qt cup mL L gal pint

Convert [AMT SOURCE_UNIT in DEST_UNIT, or (q)uit]: <b>10 mi in m</b>
10 mi = 16093.440000 m

Convert (AMT SOURCE_UNIT in DEST_UNIT, or (q)uit): <b>1 lb in oz</b>
1 lb = 16.000000 oz

Convert [AMT SOURCE_UNIT in DEST_UNIT, or (q)uit]: <b>123.456789 kg in mg</b>
123.456789 kg = 123456789.000000 mg

Convert (AMT SOURCE_UNIT in DEST_UNIT, or (q)uit): <b>q</b>
Thanks for converting with us. Y'all come back now, y'hear?</code></pre>
    </article>

    <article>
      <article id="conversion-units">
        <h3>What units do you need to support?</h3>

        <p>We would like you to support units in three categories:
          <em>distances</em>, <em>volumes</em>, and <em>weights</em> with at least
          the following units in each category:</p>

        <table style="border-collapse: collapse; margin: 0 auto">
          <tr>
            <th style="border-color: #000; border-style: solid; border-width: 0 0 1px 0">Distances</th>
            <th style="border-color: #000; border-style: solid; border-width: 0 0 1px 0">Volumes</th>
            <th style="border-color: #000; border-style: solid; border-width: 0 0 1px 0">Weights</th>
          </tr>
          <tr>
            <td style="vertical-align: top">
              <ul style="margin: 0">
                <li><strong>m</strong> (meters)</li>
                <li><strong>cm</strong> (centimeters)</li>
                <li><strong>mm</strong> (millimeters)</li>
                <li><strong>km</strong> (kilometers)</li>
                <li><strong>in</strong> (inches)</li>
                <li><strong>ft</strong> (feet)</li>
                <li><strong>yd</strong> (yards)</li>
                <li><strong>mi</strong> (miles)</li>
              </ul>
            </td>
            <td style="vertical-align: top">
              <ul style="margin: 0">
                <li><strong>L</strong> (liters)</li>
                <li><strong>mL</strong> (millileters)</li>
                <li><strong>floz</strong> (fluid ounces)</li>
                <li><strong>cup</strong> (cups)</li>
                <li><strong>pint</strong> (pints)</li>
                <li><strong>qt</strong> (quarts)</li>
                <li><strong>gal</strong> (gallons)</li>
              </ul>
            </td>
            <td style="vertical-align: top">
              <ul style="margin: 0">
                <li><strong>g</strong> (grams)</li>
                <li><strong>kg</strong> (kilograms)</li>
                <li><strong>mg</strong> (milligrams)</li>
                <li><strong>oz</strong> (ounces)</li>
                <li><strong>lb</strong> (pounds)</li>
              </ul>
            </td>
          </tr>
        </table>

        <p>You should be able to convert any amount of a unit in any category to
          any other unit in the same category.</p>
      </article>

      <article id="conversion-constants">
        <h3>Where to find the conversion constants</h3>

        <p>
          There are many websites and books with conversion constants listed, so feel free to get this
          data any way you wish. One great source is Google, which happens to have the exact same format
          for specifying these conversions. You can use Google to find out how many <em>source</em> units
          are in a <em>destination</em> unit by typing &quot;<code>1 <em>source_unit</em> in <em>destination_unit</em></code>&quot;
          and build internal tables to store these answers. See the Hints section below
          to see how we suggest you approach the internal details of storing the information.
        </p>

        <p>
          At this point, you might wonder why we're doing all this work if Google can do it for us. First,
          we don't always have access to the Internet. Second, it's a valuable beginning programming activity.
        </p>
      </article>
    </article>

    <article id="approaches">
      <h2>Potential Approaches</h2>

      <p>How should you store all of the multiplying factors between units?
        There's the <em>hard</em> way and the <em>easy</em> way to think about it.
        The particular Python data type you should use is something you should
        consider on your own.</p>

      <article>
        <h4>The Hard Way</h4>
        <p>The hard way is to build an internal two-dimensional table of the conversion of <em>every</em> unit to <em>every other</em> unit for every category. This is really painful. Imagine how many translators the <a href="http://www.un.org/">United Nations</a> would need if they had 1,000 people speaking different languages and they used this model...a million people!</p>
      </article>

      <article>
        <h4>The Easy Way</h4>
        <p>The easy way is to find a common, base unit (e.g., <strong>meters</strong> for distances) and then just store the constant (e.g., 1000 for kilometers) that is used to convert from the base unit to that unit. To convert from two units in the same category (neither of which are the base unit), you simply convert from your <code><em>SOURCE_UNIT</em></code> to the base unit, then from that to the <code><em>DESTINATION_UNIT</em></code>. Using the previous analogy you'd only need to hire 1,000 translators at the UN -- as long as each could translate to and from their language to a common language, like one of the <a href="http://en.wikipedia.org/wiki/Click_consonant">click languages</a>. For the math-minded among you, the reason this works is that the conversion among all of our units is a <a href="http://en.wikipedia.org/wiki/Linear">Linear</a>, <a href="http://en.wikipedia.org/wiki/Transitive_relation">Transitive relation</a>.</p>
      </article>
    </article>

    <article id="testing">
      <h2>Testing</h2>

      <p>Test each function in your program with enough different inputs to show
        that it works. When testing a function, print out the input, the expected
        result, and the actual result.</p>

      <p>You are required to write docstring and doctest for every function you
        write and also write comments to explain what your code is doing. You may
        refer to the previous assignment for testing related requirements.</p>
    </article>

    <article>
      <h2>Autograder</h2>

      <p>
        In the starter code, we have provided an autograder for you to verify the correctness
        of your program before submitting your project. </p>

      <p>
        Run the autograder with following command:
      </p>

      <pre v-highlightjs><code class="bash">$ python3 autograder.py</code></pre>

      <p>The autograder contains additional tests for your program and if you passed all of them,
        you should see something like:
      </p>

      <pre v-highlightjs><code class="plain">Autograder Result: 27/27 success, 0 failed.</code></pre>
    </article>

    <article id="checklist">
      <h2>Checklist</h2>

      <p>These are the requirements to meet for project completion.</p>

      <ul>
        <li>Use of functions&mdash;your program must be broken down into one or more functions&mdash;it cannot be one big long script.</li>
        <li>Each function has a docstring that summarizes its purpose and provides a description of its inputs and outputs.</li>
        <li>Tests and test output for each function in the program.</li>
        <li>All functions and variables have meaningful names.</li>
        <li>Tests showing the program produces correct translations among all the units of a category.</li>
        <li>All of the features listed in the Requirements section above.</li>
      </ul>
    </article>
  </section>

</template>



// WEBPACK FOOTER //
// src/components/assignments/python/PythonPoweredUnitConverter.vue